
  import { Component, Vue } from 'vue-property-decorator';
  import { mapGetters } from 'vuex';

  import { IErrorModel } from '@/models/errors.model';
  import { IRegisterModel } from '@/models/register.model';

  import Input from '@/components/Core/Input.vue';
  import Checkbox from '@/components/Core/Checkbox.vue';

  import accountService from '@/services/account.service';
  import recaptchaService from '@/services/recaptcha.service';
  import InputDate from '../Core/InputDate.vue';

  @Component({
    components: {
      Input,
      Checkbox,
      InputDate
    },
    computed: {
      ...mapGetters([
        'isLoading',
        'clientId',
        'privacyLinks',
      ])
    }
  })
  export default class Register extends Vue {
    data() {
      return {
        showNameSections: false,
        showBirthdateSection: true,
        showNewsletterSection: false,
        showCodeOfConductSection: false,
        studio: '',
        isRegistering: false,
        isFast: false,
        timeOnPage: new Date().getTime(),
        errors: new Array<IErrorModel>(),
        form: {} as IRegisterModel,
        privacyPolicyContent: "",
        EULAContent: "",
        EULALink: "",
        privacyPolicyLink: "",
        G2GSEGAOptInContent: "I would like to receive updates by email related to products and services, news, and invitations to events related to Games2Gether and other SEGA group companies (please tick if you consent). <br> You may opt-out of receiving marketing communications at any time by using the unsubscribe link in our emails.",
        codeOfConductContent: "I agree to <a href=\"https://community.twopointcounty.com/two-point-studios/two-point-hospital/forums/9-welcome-info/threads/2-code-of-conduct-two-point-county-guidelines?page=1#post-2\" target=\"_blank\" title=\"Code of Conduct\">the code of conduct.",
        birthdateCondition: "",
        minimumAge: undefined,
        checkMinimumAge: false,
        EULAErrorParameters: [],
      }
    };

    created() {
      this.$data.showNameSections = this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'TWO-POINT-STUDIOS';
      this.$data.showBirthdateSection = this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'TWO-POINT-STUDIOS';
      this.$data.checkMinimumAge = this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'TWO-POINT-STUDIOS';
      this.$data.minimumAge = this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'TWO-POINT-STUDIOS' ? 13 : undefined;
      this.$data.birthdateCondition = `To register, you need to be at least ${this.$data.minimumAge}.`;
      this.$data.showNewsletterSection = this.$data.RCEnvironment.VUE_APP_ENABLE_NEWSLETTER_SECTION == true;
      this.$data.showCodeOfConductSection = this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'TWO-POINT-STUDIOS';
      this.$data.studio = this.$data.RCEnvironment.VUE_APP_PARTNER_NAME;
      this.$data.showCodeOfConductSection = this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'TWO-POINT-STUDIOS';
      this.$data.form = { StudioName: this.$data.RCEnvironment.VUE_APP_REGISTER_STUDIO_NAME } as IRegisterModel;

      this.initPrivacyLinks();
      
      if (this.$route.query.referrer != null) {
        this.updateStringValue("Referrer", this.$route.query.referrer.toString());
      }
      if (this.$store.getters.accessKey != null) {
        this.$data.form.AccessKeyValue = this.$store.getters.accessKey;
      }

      this.initAgeGate();
    }

    public mounted(): void {
      this.$watch(() => this.$store.getters.ageGateSettings, (params) => {
        this.initAgeGate();
      });

      this.$watch(() => this.$store.getters.privacyLinks, (params) => {
        this.initPrivacyLinks();
      });
    }

    public getFilteredErrors(property: string = '') {
      return (this.$data.errors as Array<IErrorModel>).filter(error => error.Property == property);
    }

    public register() {
      this.$data.errors = new Array<IErrorModel>();

      if (this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == "TWO-POINT-STUDIOS" && !this.$data.form.ApproveCodeOfConduct) {
        this.$data.errors = [{ Property: "ApproveCodeOfConduct", ErrorMessage: "You must accept the Code of Conduct." } as IErrorModel];
        return;
      }

      this.$data.isFast = false;

      if (this.$data.isRegistering) {
        return;
      }

      if (this.$data.showBirthdateSection && (this.$refs.inputDateRef as InputDate).hasBadDateInput()) {
        this.$data.hasErrors = true;
        this.$data.errors.push({ Property: "DateOfBirth", ErrorMessage: "ERROR_INCOMPLETE_MANDATORY_FIELD" } as IErrorModel);
      }

      if (this.$data.environment != "CREATIVE-ASSEMBLY" && (new Date().getTime() - this.$data.timeOnPage < 15000)) {
        this.$data.isFast = true;
        this.$data.timeOnPage = new Date().getTime();
        return;
      }

      setTimeout(() => {
        if (this.$data.form.Additional || this.$data.form.UsageNameHoneyPot || this.$data.form.UsageNameHoneyPot == '') {
          return;
        }

        const error = this.$data.errors.find((err: IErrorModel) => err.Property == "DateOfBirth");
        if (error != null) {
          return;
        }

        this.$data.isRegistering = true;
        this.$store.dispatch("updateIsLoading", true);
        this.$data.errors = new Array<IErrorModel>();

        if (this.$data.RCEnvironment.VUE_APP_ENABLE_RECAPTCHA == true) {
          recaptchaService.getToken(this, "register").then(token => {
            this.$data.form.ReCAPTCHAToken = token;

            setTimeout(() => {
              this.registerRequest();
            }, 300);
          }).catch(() => {
            this.$data.isRegistering = false;
          });
        } else {
          this.registerRequest();
        }
      }, 200);
    };

    public updateStringValue(parameterName: string, value: string) {
      this.$data.form[parameterName] = value;
    }

    public updateErrorsValue(errors: Array<IErrorModel>) {
      this.$data.errors = errors;
    }

    public updateBooleanValue(parameterName: string, value: boolean) {
      this.$data.form[parameterName] = value;
    }

    public updateDateOfBirthValue(parameterName: string, value: Date) {
      this.$data.form[parameterName] = value;
    }

    private registerRequest(): void {
      const url: string = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + "register/new-user";
      this.$data.form.ClientId = this.$store.getters.clientId;

      accountService.post<IRegisterModel, object>(this, url, this.$data.form)
        .then(() => {
          this.$store.dispatch("updateIsLoading", false);
          this.$data.isRegistering = false;
          this.$router.push({ name: 'ConfirmRegister' });
        })
        .catch(errors => {
          this.$data.errors = errors.data.ErrorMessages;
          this.$data.isRegistering = false;
          this.$store.dispatch("updateIsLoading", false);
        });
    }

    private initAgeGate(): void {
      if (this.$store.getters.ageGateSettings.IsActive) {
        this.$data.showBirthdateSection = true;
        this.$data.minimumAge = this.$store.getters.ageGateSettings.LowThresholdAge;
        this.$data.birthdateCondition = `To register, you need to be at least ${this.$store.getters.ageGateSettings.LowThresholdAge}.`;
      }
    }

    private initPrivacyLinks(): void {
      this.$data.privacyPolicyLink = this.$store.getters.privacyLinks.PrivacyPolicy;
      this.$data.privacyPolicyContent = "I acknowledge that I have read and understood the practices set out in the <a href=\"" + this.$data.privacyPolicyLink + "\" target=\"_blank\" title=\"Privacy Policy\">Privacy Policy</a> (please tick).";

      this.$data.EULALink = this.$store.getters.privacyLinks.TermsOfService;
      this.$data.EULAContent = "I accept the <a href=\"" + this.$data.EULALink + "\" target=\"_blank\" title=\"G2G Account Terms\">G2G Account Terms</a> (please tick).";

      if (this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'TWO-POINT-STUDIOS') {
        this.$data.privacyPolicyContent = "Please read our <a href=\"" + this.$data.privacyPolicyLink + "\" target=\"_blank\" title=\"Privacy Policy\">Privacy Policy</a> which sets out information about our data privacy practices.";
        this.$data.EULAContent = "By creating an account, I agree to the Two Point Studios Account <a href=\"" + this.$data.EULALink + "\" target=\"_blank\" title=\"Terms of Service\">Terms of Service</a>.";
        this.$data.G2GSEGAOptInContent = "I would like to receive updates by email about products, services, news, and invitations to events from Two Point Studios and any of the SEGA group companies (please tick if you consent). You may opt-out of receiving these communications at any time by using the unsubscribe link in our emails.";
      } else if (this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'AVALANCHE' || this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'UBISOFT') {
        this.$data.privacyPolicyContent = "Please read our <a href=\"" + this.$data.privacyPolicyLink + "\" target=\"_blank\" title=\"Privacy Policy\">Privacy Policy</a> which sets out information about our data privacy practices.";
        this.$data.EULAContent = "By creating an account, I agree to the <a href=\"" + this.$data.EULALink + "\" target=\"_blank\" title=\"Terms of Service\">Terms of Service</a>.";
      } else if (this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'STAGING' || this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'TEST' || this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'PROD') {
        this.$data.privacyPolicyContent = "I acknowledge that I have read and understood the practices set out in the <a href=\"" + this.$data.privacyPolicyLink + "\" target=\"_blank\" title=\"Privacy Policy\">Privacy Policy</a>.";
        this.$data.EULAContent = "I accept the <a href=\"" + this.$data.EULALink + "\" target=\"_blank\" title=\"Terms of Use\">Terms of Use</a>.";
        this.$data.G2GSEGAOptInContent = "I would like to receive updates by email related to products and services, news, and invitations to events related to Amplitude. <br> You may opt-out of receiving marketing communications at any time by using the unsubscribe link in our emails.";
        this.$data.EULAErrorParameters = ['Terms of Use'];
      }
    }
  }
